import { useCallback, useReducer } from "react";
import type { RejectionOutcome } from "./ConfirmStatusChangeModal";

/**
 * What a reviewer has typed in the Reject box for ONE film, kept until the
 * rejection is sent (DW-030, 25/10). Cancel and Escape used to throw the words
 * away, so pressing Reject again showed an empty box.
 *
 * Kept per film (the film's id is the key), in memory for the life of the
 * page's session in the browser tab: it survives Cancel, Escape, moving to
 * another film and back, and going between the films list and the film page.
 * It does not survive a reload (nothing typed here is stored anywhere else),
 * and it is cleared the moment the rejection goes through. It never writes
 * to a film: it is only the text in a box.
 */
export interface RejectionDraft {
  reason: string;
  outcome: RejectionOutcome | null;
}

const EMPTY: RejectionDraft = { reason: "", outcome: null };
const drafts = new Map<string, RejectionDraft>();

export const getRejectionDraft = (filmId?: string | null): RejectionDraft =>
  (filmId && drafts.get(filmId)) || EMPTY;

export const setRejectionDraft = (filmId: string, patch: Partial<RejectionDraft>) => {
  drafts.set(filmId, { ...getRejectionDraft(filmId), ...patch });
};

export const clearRejectionDraft = (filmId?: string | null) => {
  if (filmId) drafts.delete(filmId);
};

/** Forget every draft (tests, and sign-out: see store/index.ts). */
export const resetRejectionDrafts = () => drafts.clear();

export function useRejectionDraft(filmId?: string | null) {
  const [, refresh] = useReducer((n: number) => n + 1, 0);
  const draft = getRejectionDraft(filmId);

  const set = useCallback(
    (patch: Partial<RejectionDraft>) => {
      if (!filmId) return;
      setRejectionDraft(filmId, patch);
      refresh();
    },
    [filmId]
  );

  return {
    reason: draft.reason,
    outcome: draft.outcome,
    setReason: useCallback((reason: string) => set({ reason }), [set]),
    setOutcome: useCallback((outcome: RejectionOutcome | null) => set({ outcome }), [set]),
    /** Call when the rejection has been sent. */
    clear: useCallback(() => {
      clearRejectionDraft(filmId);
      refresh();
    }, [filmId]),
  };
}
